import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { ProductListItem } from '../../types/ProductTypes';
import { highlightMatch } from '../../utils/searchUtils';
import { formatCategoryName } from '../../utils/categoryNameFormatter';
import { marpolRegulationToFAIcon } from '../../utils/iconMapper';
import {
    CardListWrapper,
    CardListContainer,
    CardListEmptyMessage,
    CardListLoadingMessage,
    CardItem,
    CardItemName,
    CardItemIcon,
    CardItemCategory,
    CardItemDetail,
    CardItemTypeIcon,
    CardItemContent,
} from './ProductCardList.styles';

interface ProductCardListProps {
    items: ProductListItem[];
    activeProductId?: number;
    onProductClick: (product: ProductListItem) => void;
    emptyMessage?: string;
    isLoading?: boolean;
    loadingMessage?: string;
    header?: React.ReactNode;
    searchTerm?: string;
}

const ProductCardList: React.FC<ProductCardListProps> = ({
    items,
    activeProductId,
    onProductClick,
    emptyMessage = 'No products.',
    isLoading = false,
    loadingMessage = 'Loading...',
    header,
    searchTerm = '',
}) => {
    const renderContent = () => {
        if (isLoading) {
            return <CardListLoadingMessage>{loadingMessage}</CardListLoadingMessage>;
        }
        if (items.length === 0) {
            return <CardListEmptyMessage>{emptyMessage}</CardListEmptyMessage>;
        }
        return items.map((product) => {
            const isCategory = !product.tradeName && !product.chartererName;
            const marpolRegNum = parseInt(product.marpolRegulation || '0', 10);
            const marpolIcon = marpolRegulationToFAIcon(marpolRegNum);
            // Format category name: replace trade name/contains placeholders, or strip them for categories
            const formattedCategory = formatCategoryName(product.categoryName, product.tradeName, product.contains);
            return (
                <CardItem
                    key={product.id}
                    $isActive={product.id === activeProductId}
                    onClick={() => onProductClick(product)}
                    title={`${product.tradeName || formattedCategory}${product.chartererName ? ` (${product.chartererName})` : ''}${product.casNumber ? `\nCAS: ${product.casNumber}` : ''}`}
                >
                    <CardItemIcon>
                        <FontAwesomeIcon
                            icon={marpolIcon.icon as any}
                            style={{ color: marpolIcon.color, fontSize: '14px' }}
                        />
                    </CardItemIcon>
                    <CardItemContent>
                        <CardItemName>
                            {highlightMatch(product.tradeName || formattedCategory, searchTerm)}
                        </CardItemName>
                        <CardItemCategory>
                            {highlightMatch(formattedCategory, searchTerm)}
                        </CardItemCategory>
                        {product.chartererName && (
                            <CardItemDetail>Charterer: {highlightMatch(product.chartererName, searchTerm)}</CardItemDetail>
                        )}
                    </CardItemContent>
                    <CardItemTypeIcon title={isCategory ? 'Product Category' : 'Product'}>
                        <FontAwesomeIcon
                            icon={isCategory ? faLayerGroup : faBox}
                            style={{ color: isCategory ? '#6c757d' : '#0d6efd', fontSize: '13px' }}
                        />
                    </CardItemTypeIcon>
                </CardItem>
            );
        });
    };

    return (
        <CardListWrapper>
            {header}
            <CardListContainer>
                {renderContent()}
            </CardListContainer>
        </CardListWrapper>
    );
};

export default ProductCardList;
